import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, AlertCircle, DollarSign, Plus, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Table, { TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import {
  useTodaysPunches,
  usePendingExceptions,
  useApproveException,
  useRejectException,
  useAgentsWithPayRates,
  useCreateManualPunch,
  useCreatePayRate,
  useUpdatePayRate,
  useBulkUpdatePayRates,
  useUpdatePunch,
  useDeletePunch,
} from '../../hooks/useTimePunches';
import { PunchType, PunchExceptionType } from '../../services/time-punches.service';
import { useAgents } from '../../hooks/useAgents';

export default function AdminTimePunchesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive active tab directly from URL (single source of truth)
  const urlTab = searchParams.get('tab');
  const activeTab = urlTab || 'today';

  // Handler to change tabs - directly updates URL
  const handleTabChange = (tab: string) => {
    setSearchParams({ tab }, { replace: true });
  };

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Modals
  const [isManualPunchModalOpen, setIsManualPunchModalOpen] = useState(false);
  const [isPayRateModalOpen, setIsPayRateModalOpen] = useState(false);
  const [isEditPayRateModalOpen, setIsEditPayRateModalOpen] = useState(false);
  const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
  const [selectedPunch, setSelectedPunch] = useState<any>(null);
  const [selectedPayRate, setSelectedPayRate] = useState<any>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Time Tracking</h1>
          <p className="text-gray-600 mt-1">Manage team time punches and attendance</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setIsManualPunchModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Manual Punch
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="today">
            <Clock className="w-4 h-4 mr-2" />
            Time Punches
          </TabsTrigger>
          <TabsTrigger value="exceptions">
            <AlertCircle className="w-4 h-4 mr-2" />
            Exceptions
          </TabsTrigger>
          <TabsTrigger value="payrates">
            <DollarSign className="w-4 h-4 mr-2" />
            Pay Rates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today">
          <TodaysPunchesTab selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
        </TabsContent>

        <TabsContent value="exceptions">
          <ExceptionsTab
            setSelectedPunch={setSelectedPunch}
            setIsExceptionModalOpen={setIsExceptionModalOpen}
          />
        </TabsContent>

        <TabsContent value="payrates">
          <PayRatesTab
            setIsPayRateModalOpen={setIsPayRateModalOpen}
            setSelectedPayRate={setSelectedPayRate}
            setIsEditPayRateModalOpen={setIsEditPayRateModalOpen}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ManualPunchModal
        isOpen={isManualPunchModalOpen}
        onClose={() => setIsManualPunchModalOpen(false)}
      />
      <PayRateModal
        isOpen={isPayRateModalOpen}
        onClose={() => setIsPayRateModalOpen(false)}
      />
      <EditPayRateModal
        isOpen={isEditPayRateModalOpen}
        onClose={() => {
          setIsEditPayRateModalOpen(false);
          setSelectedPayRate(null);
        }}
        payRate={selectedPayRate}
      />
      <ExceptionApprovalModal
        isOpen={isExceptionModalOpen}
        onClose={() => {
          setIsExceptionModalOpen(false);
          setSelectedPunch(null);
        }}
        punch={selectedPunch}
      />
    </div>
  );
}

// Today's Punches Tab
function TodaysPunchesTab({ selectedDate, setSelectedDate }: any) {
  const { data: punches = [], isLoading } = useTodaysPunches(selectedDate);
  const updatePunch = useUpdatePunch();
  const deletePunch = useDeletePunch();
  const [editingPunch, setEditingPunch] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<any>({});

  const getPunchTypeBadge = (type: PunchType) => {
    switch (type) {
      case PunchType.CLOCK_IN:
        return <Badge variant="success">Clock In</Badge>;
      case PunchType.CLOCK_OUT:
        return <Badge variant="error">Clock Out</Badge>;
      case PunchType.BREAK_START:
        return <Badge variant="warning">Break Start</Badge>;
      case PunchType.BREAK_END:
        return <Badge variant="info">Break End</Badge>;
      case PunchType.LUNCH_START:
        return <Badge variant="warning">Lunch Start</Badge>;
      case PunchType.LUNCH_END:
        return <Badge variant="info">Lunch End</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toISOString().slice(0, 16);
  };

  const startEditing = (punch: any) => {
    setEditingPunch(punch.id);
    setEditValues({
      punchType: punch.punchType,
      punchTime: formatDateTime(punch.punchTime),
    });
  };

  const cancelEditing = () => {
    setEditingPunch(null);
    setEditValues({});
  };

  const savePunch = async (punchId: string) => {
    try {
      await updatePunch.mutateAsync({
        id: punchId,
        updates: {
          punchType: editValues.punchType,
          punchTime: new Date(editValues.punchTime).toISOString(),
        },
      });
      setEditingPunch(null);
      setEditValues({});
    } catch (error) {
      console.error('Failed to update punch:', error);
      alert('Failed to update punch');
    }
  };

  const handleDelete = async (punchId: string) => {
    if (confirm('Are you sure you want to delete this punch?')) {
      try {
        await deletePunch.mutateAsync(punchId);
      } catch (error) {
        console.error('Failed to delete punch:', error);
        alert('Failed to delete punch');
      }
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Filter by Date</h3>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-48"
          />
        </div>
      </Card>

      <Card padding="none">
        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Loading punches...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Exception</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {punches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-12">
                    No punches found for this date
                  </TableCell>
                </TableRow>
              ) : (
                punches.map((punch: any) => {
                  const isEditing = editingPunch === punch.id;

                  return (
                    <TableRow key={punch.id}>
                      <TableCell>
                        {punch.agent ? `${punch.agent.firstName} ${punch.agent.lastName}` : 'Unknown'}
                      </TableCell>
                      <TableCell>{punch.agent?.employeeId || '-'}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Select
                            value={editValues.punchType}
                            onChange={(e) => setEditValues({ ...editValues, punchType: e.target.value })}
                            options={[
                              { value: PunchType.CLOCK_IN, label: 'Clock In' },
                              { value: PunchType.CLOCK_OUT, label: 'Clock Out' },
                              { value: PunchType.BREAK_START, label: 'Break Start' },
                              { value: PunchType.BREAK_END, label: 'Break End' },
                              { value: PunchType.LUNCH_START, label: 'Lunch Start' },
                              { value: PunchType.LUNCH_END, label: 'Lunch End' },
                            ]}
                          />
                        ) : (
                          getPunchTypeBadge(punch.punchType)
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="datetime-local"
                            value={editValues.punchTime}
                            onChange={(e) => setEditValues({ ...editValues, punchTime: e.target.value })}
                            className="w-48"
                          />
                        ) : (
                          formatTime(punch.punchTime)
                        )}
                      </TableCell>
                      <TableCell>
                        {punch.status === 'APPROVED' && <Badge variant="success">Approved</Badge>}
                        {punch.status === 'PENDING' && <Badge variant="default">Pending</Badge>}
                        {punch.status === 'EXCEPTION' && <Badge variant="error">Exception</Badge>}
                        {punch.status === 'REJECTED' && <Badge variant="error">Rejected</Badge>}
                      </TableCell>
                      <TableCell>
                        {punch.exceptionType ? (
                          <span className="text-sm text-red-600">{punch.exceptionType.replace(/_/g, ' ')}</span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => savePunch(punch.id)}
                                disabled={updatePunch.isPending}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={cancelEditing}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => startEditing(punch)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleDelete(punch.id)}
                                disabled={deletePunch.isPending}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// Exceptions Tab
function ExceptionsTab({ setSelectedPunch, setIsExceptionModalOpen }: any) {
  const { data: exceptions = [], isLoading } = usePendingExceptions();

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getExceptionBadge = (type: PunchExceptionType) => {
    const text = type.replace(/_/g, ' ');
    if (type.includes('MISSED')) return <Badge variant="error">{text}</Badge>;
    if (type.includes('LATE')) return <Badge variant="warning">{text}</Badge>;
    if (type.includes('EARLY')) return <Badge variant="warning">{text}</Badge>;
    if (type.includes('LONG')) return <Badge variant="warning">{text}</Badge>;
    if (type === PunchExceptionType.OVERTIME) return <Badge variant="info">{text}</Badge>;
    return <Badge variant="default">{text}</Badge>;
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Pending Exceptions</h3>
            <p className="text-sm text-gray-600">Review and approve/reject time punch exceptions</p>
          </div>
          <Badge variant="error">{exceptions.length} Pending</Badge>
        </div>
      </Card>

      <Card padding="none">
        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Loading exceptions...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Exception Type</TableHead>
                <TableHead>Punch Time</TableHead>
                <TableHead>Expected Time</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exceptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-12">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p>No pending exceptions</p>
                  </TableCell>
                </TableRow>
              ) : (
                exceptions.map((punch: any) => (
                  <TableRow key={punch.id}>
                    <TableCell>
                      {punch.agent ? `${punch.agent.firstName} ${punch.agent.lastName}` : 'Unknown'}
                    </TableCell>
                    <TableCell>{punch.exceptionType && getExceptionBadge(punch.exceptionType)}</TableCell>
                    <TableCell>{formatTime(punch.punchTime)}</TableCell>
                    <TableCell>{punch.expectedTime ? formatTime(punch.expectedTime) : '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{punch.exceptionNotes || '-'}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          setSelectedPunch(punch);
                          setIsExceptionModalOpen(true);
                        }}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// Pay Rates Tab
function PayRatesTab({ setIsPayRateModalOpen: _setIsPayRateModalOpen, setSelectedPayRate: _setSelectedPayRate, setIsEditPayRateModalOpen: _setIsEditPayRateModalOpen }: {
  setIsPayRateModalOpen: (value: boolean) => void;
  setSelectedPayRate: (value: any) => void;
  setIsEditPayRateModalOpen: (value: boolean) => void;
}) {
  const { data: agentsWithRates = [], isLoading } = useAgentsWithPayRates();
  const bulkUpdate = useBulkUpdatePayRates();
  const [editingRates, setEditingRates] = useState<Record<string, string>>({});
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [bulkRateValue, setBulkRateValue] = useState('');

  const handleRateChange = (agentId: string, value: string) => {
    setEditingRates(prev => ({ ...prev, [agentId]: value }));
  };

  const handleSaveRate = async (agentId: string) => {
    const rateValue = editingRates[agentId];
    if (!rateValue) return;

    const hourlyRate = parseFloat(rateValue);
    if (isNaN(hourlyRate) || hourlyRate <= 0) {
      alert('Please enter a valid rate');
      return;
    }

    try {
      await bulkUpdate.mutateAsync([{ agentId, hourlyRate }]);
      setEditingRates(prev => {
        const newRates = { ...prev };
        delete newRates[agentId];
        return newRates;
      });
    } catch (error) {
      console.error('Failed to update rate:', error);
      alert('Failed to update rate');
    }
  };

  const toggleAgentSelection = (agentId: string) => {
    setSelectedAgents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAgents.size === agentsWithRates.length) {
      setSelectedAgents(new Set());
    } else {
      setSelectedAgents(new Set(agentsWithRates.map((a: any) => a.id)));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedAgents.size === 0) {
      alert('Please select at least one agent');
      return;
    }

    const hourlyRate = parseFloat(bulkRateValue);
    if (isNaN(hourlyRate) || hourlyRate <= 0) {
      alert('Please enter a valid rate');
      return;
    }

    const updates = Array.from(selectedAgents).map(agentId => ({ agentId, hourlyRate }));

    try {
      await bulkUpdate.mutateAsync(updates);
      setSelectedAgents(new Set());
      setBulkRateValue('');
      alert(`Successfully updated ${updates.length} pay rates`);
    } catch (error) {
      console.error('Failed to bulk update:', error);
      alert('Failed to update pay rates');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">Agent Pay Rates</h3>
            <p className="text-sm text-gray-600">Manage hourly rates for all agents</p>
          </div>
        </div>
      </Card>

      {selectedAgents.size > 0 && (
        <Card>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">{selectedAgents.size} agent(s) selected</span>
            <div className="flex items-center gap-2 flex-1">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bulkRateValue}
                onChange={(e) => setBulkRateValue(e.target.value)}
                placeholder="Enter hourly rate"
                className="w-48"
              />
              <Button
                onClick={handleBulkUpdate}
                disabled={bulkUpdate.isPending || !bulkRateValue}
                size="sm"
              >
                {bulkUpdate.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  `Update Selected (${selectedAgents.size})`
                )}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedAgents(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card padding="none">
        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Loading agents...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selectedAgents.size === agentsWithRates.length && agentsWithRates.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Current Hourly Rate</TableHead>
                <TableHead>Effective Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agentsWithRates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-12">
                    No agents found
                  </TableCell>
                </TableRow>
              ) : (
                agentsWithRates.map((agent: any) => {
                  const currentRate = agent.currentPayRate?.hourlyRate;
                  const effectiveDate = agent.currentPayRate?.effectiveDate;
                  const isEditing = agent.id in editingRates;
                  const editValue = editingRates[agent.id] || String(currentRate || '');

                  return (
                    <TableRow key={agent.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedAgents.has(agent.id)}
                          onChange={() => toggleAgentSelection(agent.id)}
                          className="rounded border-gray-300"
                        />
                      </TableCell>
                      <TableCell>
                        {agent.firstName} {agent.lastName}
                      </TableCell>
                      <TableCell>{agent.employeeId || '-'}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editValue}
                            onChange={(e) => handleRateChange(agent.id, e.target.value)}
                            className="w-32"
                            autoFocus
                          />
                        ) : (
                          <span className={currentRate ? 'font-semibold' : 'text-gray-400'}>
                            {currentRate ? `$${Number(currentRate).toFixed(2)}/hr` : 'Not set'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {effectiveDate ? new Date(effectiveDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => handleSaveRate(agent.id)}
                                disabled={bulkUpdate.isPending}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setEditingRates(prev => {
                                    const newRates = { ...prev };
                                    delete newRates[agent.id];
                                    return newRates;
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleRateChange(agent.id, String(currentRate || ''))}
                            >
                              {currentRate ? 'Edit' : 'Set Rate'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// Manual Punch Modal
function ManualPunchModal({ isOpen, onClose }: any) {
  const { data: agents = [] } = useAgents();
  const createPunch = useCreateManualPunch();
  const [formData, setFormData] = useState({
    agentId: '',
    punchType: PunchType.CLOCK_IN,
    punchTime: new Date().toISOString().slice(0, 16),
    isManual: true,
    exceptionNotes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createPunch.mutateAsync(formData as any);
      onClose();
      setFormData({
        agentId: '',
        punchType: PunchType.CLOCK_IN,
        punchTime: new Date().toISOString().slice(0, 16),
        isManual: true,
        exceptionNotes: '',
      });
    } catch (error) {
      console.error('Failed to create punch:', error);
      alert('Failed to create punch: ' + (error as any)?.response?.data?.message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Manual Punch">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
          <Select
            value={formData.agentId}
            onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
            options={[
              { value: '', label: 'Select an agent' },
              ...agents.map((agent: any) => ({
                value: agent.id,
                label: `${agent.firstName} ${agent.lastName} (${agent.employeeId})`,
              })),
            ]}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Punch Type</label>
          <Select
            value={formData.punchType}
            onChange={(e) => setFormData({ ...formData, punchType: e.target.value as PunchType })}
            options={[
              { value: PunchType.CLOCK_IN, label: 'Clock In' },
              { value: PunchType.CLOCK_OUT, label: 'Clock Out' },
              { value: PunchType.BREAK_START, label: 'Break Start' },
              { value: PunchType.BREAK_END, label: 'Break End' },
              { value: PunchType.LUNCH_START, label: 'Lunch Start' },
              { value: PunchType.LUNCH_END, label: 'Lunch End' },
            ]}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Punch Time</label>
          <Input
            type="datetime-local"
            value={formData.punchTime}
            onChange={(e) => setFormData({ ...formData, punchTime: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={3}
            value={formData.exceptionNotes}
            onChange={(e) => setFormData({ ...formData, exceptionNotes: e.target.value })}
            placeholder="Add any notes about this manual punch..."
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createPunch.isPending}>
            {createPunch.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Punch'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Pay Rate Modal
function PayRateModal({ isOpen, onClose }: any) {
  const { data: agents = [] } = useAgents();
  const createPayRate = useCreatePayRate();
  const [formData, setFormData] = useState({
    agentId: '',
    hourlyRate: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createPayRate.mutateAsync({
        ...formData,
        hourlyRate: parseFloat(formData.hourlyRate),
      } as any);
      onClose();
      setFormData({
        agentId: '',
        hourlyRate: '',
        effectiveDate: new Date().toISOString().split('T')[0],
        notes: '',
      });
    } catch (error) {
      console.error('Failed to create pay rate:', error);
      alert('Failed to create pay rate: ' + (error as any)?.response?.data?.message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Pay Rate">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
          <Select
            value={formData.agentId}
            onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
            options={[
              { value: '', label: 'Select an agent' },
              ...agents.map((agent: any) => ({
                value: agent.id,
                label: `${agent.firstName} ${agent.lastName} (${agent.employeeId})`,
              })),
            ]}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.hourlyRate}
            onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
            placeholder="25.00"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
          <Input
            type="date"
            value={formData.effectiveDate}
            onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Add any notes about this pay rate change..."
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createPayRate.isPending}>
            {createPayRate.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Pay Rate'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Pay Rate Modal
function EditPayRateModal({ isOpen, onClose, payRate }: any) {
  const updatePayRate = useUpdatePayRate();
  const [formData, setFormData] = useState({
    hourlyRate: '',
    notes: '',
  });

  // Update form when payRate changes
  useEffect(() => {
    if (payRate) {
      setFormData({
        hourlyRate: payRate.hourlyRate?.toString() || '',
        notes: payRate.notes || '',
      });
    }
  }, [payRate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payRate) return;

    try {
      await updatePayRate.mutateAsync({
        id: payRate.id,
        updates: {
          hourlyRate: parseFloat(formData.hourlyRate),
          notes: formData.notes,
        },
      });
      onClose();
    } catch (error) {
      console.error('Failed to update pay rate:', error);
      alert('Failed to update pay rate: ' + (error as any)?.response?.data?.message);
    }
  };

  if (!payRate) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Pay Rate">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
          <Input
            value={payRate.agent ? `${payRate.agent.firstName} ${payRate.agent.lastName}` : 'Unknown'}
            disabled
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.hourlyRate}
            onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Add any notes..."
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={updatePayRate.isPending}>
            {updatePayRate.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Pay Rate'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Exception Approval Modal
function ExceptionApprovalModal({ isOpen, onClose, punch }: any) {
  const approveException = useApproveException();
  const rejectException = useRejectException();
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const handleApprove = async () => {
    if (punch) {
      try {
        await approveException.mutateAsync({ id: punch.id, dto: { notes } });
        alert('Exception approved successfully!');
        onClose();
        setNotes('');
      } catch (error) {
        console.error('Failed to approve exception:', error);
        alert('Failed to approve: ' + (error as any)?.response?.data?.message);
      }
    }
  };

  const handleReject = async () => {
    if (punch && rejectionReason) {
      try {
        await rejectException.mutateAsync({ id: punch.id, dto: { reason: rejectionReason } });
        alert('Exception rejected successfully!');
        onClose();
        setRejectionReason('');
      } catch (error) {
        console.error('Failed to reject exception:', error);
        alert('Failed to reject: ' + (error as any)?.response?.data?.message);
      }
    }
  };

  if (!punch) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Review Exception">
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div>
            <p className="text-sm font-medium text-gray-700">Agent</p>
            <p className="text-sm text-gray-900">
              {punch.agent ? `${punch.agent.firstName} ${punch.agent.lastName}` : 'Unknown'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Exception Type</p>
            <p className="text-sm text-gray-900">{punch.exceptionType?.replace(/_/g, ' ')}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Punch Time</p>
            <p className="text-sm text-gray-900">{new Date(punch.punchTime).toLocaleString()}</p>
          </div>
          {punch.expectedTime && (
            <div>
              <p className="text-sm font-medium text-gray-700">Expected Time</p>
              <p className="text-sm text-gray-900">{new Date(punch.expectedTime).toLocaleString()}</p>
            </div>
          )}
          {punch.exceptionNotes && (
            <div>
              <p className="text-sm font-medium text-gray-700">Agent Notes</p>
              <p className="text-sm text-gray-900">{punch.exceptionNotes}</p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Approval Notes (Optional)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this approval..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rejection Reason (Required if rejecting)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Required if rejecting..."
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!rejectionReason || rejectException.isPending}
          >
            {rejectException.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Rejecting...
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </>
            )}
          </Button>
          <Button onClick={handleApprove} disabled={approveException.isPending}>
            {approveException.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Approving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
